import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, ClipboardCheck } from "lucide-react";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHALLENGES = [
  "Getting enough buyer and seller leads",
  "Responding to new inquiries quickly",
  "Following up with leads consistently",
  "Qualifying which leads are ready to move",
  "Getting interested prospects to book appointments",
  "Knowing what happened to each lead",
  "I'm not sure",
];

const LEAD_SOURCES = [
  "Website",
  "Portal leads",
  "Social media leads",
  "Phone inquiries",
  "Open-house leads",
];

const SINGLE_SELECT_GROUPS = [
  {
    key: "operation_type",
    label: "Are you an individual agent, or part of a team/brokerage?",
    options: ["Individual agent", "Team", "Brokerage"],
  },
  {
    key: "lead_type_focus",
    label: "Do you mostly work with buyers, sellers, or both?",
    options: ["Buyers", "Sellers", "Both"],
  },
  {
    key: "lead_volume",
    label: "Approximate number of new leads per month",
    options: ["1–10", "11–25", "26–50", "51–100", "100+"],
  },
  {
    key: "response_time",
    label: "Typical initial response time to a new inquiry",
    options: ["Under 5 minutes", "5–30 minutes", "Within a few hours", "Same day", "Next day or later", "It varies"],
  },
  {
    key: "follow_up_process",
    label: "Current follow-up process",
    options: ["Automated follow-up sequence", "CRM tasks and reminders", "Manual follow-up", "No defined process"],
  },
  {
    key: "follow_up_attempts",
    label: "Approximate number of follow-up attempts per lead",
    options: ["0–1", "2–3", "4–6", "7+"],
  },
  {
    key: "booking_process",
    label: "How appointments and showings get booked",
    options: ["Online scheduling link", "Manual back-and-forth", "Mix of both", "No set process"],
  },
  {
    key: "after_hours",
    label: "What happens to after-hours inquiries",
    options: ["Answered right away", "Handled the next business day", "Often missed", "Not sure"],
  },
  {
    key: "dormant_leads",
    label: "How older or dormant leads are handled",
    options: ["Regular nurture campaign", "Occasional outreach", "Rarely revisited", "Not sure"],
  },
];

const initial = {
  first_name: "",
  business_name: "",
  business_email: "",
  website_url: "",
  no_website: false,
  crm_used: "",
  average_customer_value: "",
  monthly_inquiries: "",
  estimated_close_rate: "",
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-[box-shadow,border-color] duration-200";

const labelCls = "block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider";

const pillCls = (active) =>
  `rounded-full px-5 py-2.5 text-sm font-semibold border transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${
    active
      ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-200"
      : "bg-white/5 border-white/15 text-slate-300 hover:border-white/35"
  }`;

export default function LeadFlowAudit() {
  const [form, setForm] = useState(initial);
  const [challenges, setChallenges] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [picks, setPicks] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    track("lead_flow_audit_opened");
  }, []);

  // Scorecard -> audit handoff: apply self-reported scorecard answers carried via
  // sessionStorage (never the URL). Everything stays editable; a notice flags the source.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("ivibes_audit_prefill");
      if (!raw) return;
      sessionStorage.removeItem("ivibes_audit_prefill");
      const p = JSON.parse(raw);
      if (p.picks && typeof p.picks === "object") {
        setPicks((prev) => ({ ...prev, ...p.picks }));
      }
      if (p.operation_type) {
        setPicks((prev) => ({ ...prev, operation_type: p.operation_type }));
      }
      if (Array.isArray(p.challenges) && p.challenges.length) {
        setChallenges((prev) => [...new Set([...prev, ...p.challenges])]);
      }
      if (p.first_name || p.business_email) {
        setForm((f) => ({
          ...f,
          first_name: p.first_name || f.first_name,
          business_email: p.business_email || f.business_email,
        }));
      }
      setPrefilled(true);
    } catch {}
  }, []);

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("lead_flow_audit_started");
    }
  };

  const set = (k) => (e) => {
    markStarted();
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const toggleChallenge = (c) => {
    markStarted();
    setChallenges((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const toggleSource = (s) => {
    markStarted();
    setLeadSources((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const pick = (key, value) => {
    markStarted();
    setPicks((prev) => ({ ...prev, [key]: prev[key] === value ? undefined : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = {
      first_name: form.first_name.trim(),
      business_name: form.business_name.trim(),
      business_email: form.business_email.trim(),
      website_url: form.no_website ? null : form.website_url.trim() || null,
      no_website: form.no_website,
      selected_challenges: challenges,
      lead_sources: leadSources,
      crm_used: form.crm_used.trim() || null,
      average_customer_value: form.average_customer_value === "" ? null : Number(form.average_customer_value),
      monthly_inquiries: form.monthly_inquiries === "" ? null : Number(form.monthly_inquiries),
      estimated_close_rate: form.estimated_close_rate === "" ? null : Number(form.estimated_close_rate),
    };
    for (const g of SINGLE_SELECT_GROUPS) {
      payload[g.key] = picks[g.key] || null;
    }
    try {
      await axios.post(`${API}/audits`, payload);
      track("lead_flow_audit_submitted");
      if (payload.average_customer_value !== null || payload.monthly_inquiries !== null || payload.estimated_close_rate !== null) {
        track("lead_flow_audit_optional_financials_completed");
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Something went wrong submitting your audit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="lead-flow-audit-page" className="min-h-screen bg-[#0B0F19]">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="audit-logo" className="flex items-center gap-2.5">
            <span className="bg-white/95 rounded-xl px-2 py-1 flex items-center">
              <img src="/ivibes-cloud.png" alt="iVIBES logo" className="h-7 w-auto" />
            </span>
            <span className="font-heading text-xl tracking-tight font-extrabold">
              <span className="text-cyan-400">i</span>VIBES
              <span className="text-xl font-medium text-slate-200 ml-1.5 tracking-normal">Consulting</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="audit-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-cyan-300 transition-colors duration-200"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-14 md:py-20">
        {done ? (
          <div data-testid="audit-confirmation" className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center mb-6">
              <ClipboardCheck size={30} />
            </div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">
              Your Real Estate Lead Flow Audit is being prepared.
            </h1>
            <p className="mt-6 text-lg text-slate-200 leading-relaxed">
              Thanks, {form.first_name.trim().split(/\s+/)[0] || "there"} — we've received the
              information for <span className="font-semibold text-white">{form.business_name}</span>.
            </p>
            <p className="mt-4 text-base text-slate-300 leading-relaxed">
              Our system will perform an initial review, and iVIBES will personally review the
              findings before your audit is delivered.
            </p>
            <p className="mt-4 text-base text-slate-300 leading-relaxed">
              You'll usually receive the results by email within 24 hours — most audits are
              completed the same business day.
            </p>
            <Link
              to="/"
              data-testid="audit-confirmation-home-link"
              className="mt-10 inline-flex items-center rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white transition-colors duration-200"
            >
              Back to Home
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Free <span className="text-gradient">Real Estate Lead Flow Audit™</span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed">
              See where buyer and seller opportunities may be slipping through the cracks between
              inquiry, response, follow-up and appointment.
            </p>
            <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
              Answer a few simple questions about your current lead flow. We'll review how it works
              today and identify the areas most likely to deserve attention.
            </p>
            <p className="mt-4 text-base text-slate-300 leading-relaxed border-l-2 border-cyan-400 pl-4">
              Every audit is personally reviewed by iVIBES Consulting before it is delivered — this
              is not an instant, auto-generated report.
            </p>

            <form data-testid="audit-form" onSubmit={submit} className="mt-12 space-y-5">
              {prefilled && (
                <div
                  data-testid="audit-prefill-notice"
                  className="rounded-2xl border border-cyan-400/30 bg-cyan-500/[0.07] p-4 text-sm text-cyan-200 leading-relaxed"
                >
                  Pre-filled from your Lead Leakage Scorecard answers. These are your own
                  self-reported answers — please review and edit anything before submitting.
                </div>
              )}
              <div>
                <label htmlFor="first_name" className={labelCls}>First Name</label>
                <input id="first_name" data-testid="audit-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="Jordan" className={inputCls} />
              </div>
              <div>
                <label htmlFor="business_name" className={labelCls}>Business Name</label>
                <input id="business_name" data-testid="audit-business-name" required value={form.business_name} onChange={set("business_name")} placeholder="Your Realty Team or Brokerage" className={inputCls} />
              </div>
              <div>
                <label htmlFor="business_email" className={labelCls}>Business Email</label>
                <input id="business_email" data-testid="audit-business-email" type="email" required value={form.business_email} onChange={set("business_email")} placeholder="jordan@yourrealty.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="website_url" className={labelCls}>Website</label>
                <input
                  id="website_url"
                  data-testid="audit-website"
                  type="text"
                  required={!form.no_website}
                  disabled={form.no_website}
                  value={form.website_url}
                  onChange={set("website_url")}
                  placeholder="https://yourrealty.com"
                  className={`${inputCls} ${form.no_website ? "opacity-40" : ""}`}
                />
                <label className="mt-2.5 flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="audit-no-website"
                    checked={form.no_website}
                    onChange={(e) => {
                      markStarted();
                      setForm((f) => ({ ...f, no_website: e.target.checked, website_url: e.target.checked ? "" : f.website_url }));
                    }}
                    className="h-4 w-4 rounded border-white/20 bg-white/5 accent-cyan-500"
                  />
                  I don't currently have a website
                </label>
              </div>

              {SINGLE_SELECT_GROUPS.slice(0, 3).map((g) => (
                <div key={g.key}>
                  <p className={labelCls}>{g.label}</p>
                  <div className="flex flex-wrap gap-2.5">
                    {g.options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        data-testid={`audit-${g.key.replace(/_/g, "-")}-${slug(o)}`}
                        aria-pressed={picks[g.key] === o}
                        onClick={() => pick(g.key, o)}
                        className={pillCls(picks[g.key] === o)}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div>
                <p className={labelCls}>Primary lead sources</p>
                <p className="text-xs text-slate-500 mb-3">Select all that apply.</p>
                <div className="flex flex-wrap gap-2.5">
                  {LEAD_SOURCES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`audit-lead-source-${slug(s)}`}
                      aria-pressed={leadSources.includes(s)}
                      onClick={() => toggleSource(s)}
                      className={pillCls(leadSources.includes(s))}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {SINGLE_SELECT_GROUPS.slice(3).map((g) => (
                <div key={g.key}>
                  <p className={labelCls}>{g.label}</p>
                  <div className="flex flex-wrap gap-2.5">
                    {g.options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        data-testid={`audit-${g.key.replace(/_/g, "-")}-${slug(o)}`}
                        aria-pressed={picks[g.key] === o}
                        onClick={() => pick(g.key, o)}
                        className={pillCls(picks[g.key] === o)}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div>
                <label htmlFor="crm_used" className={labelCls}>CRM you use (if any)</label>
                <input id="crm_used" data-testid="audit-crm" value={form.crm_used} onChange={set("crm_used")} placeholder="e.g., Follow Up Boss, kvCORE, Sierra Interactive, LionDesk" className={inputCls} />
              </div>

              <div>
                <p className={labelCls}>Where do you feel the biggest challenge is?</p>
                <p className="text-xs text-slate-500 mb-3">Select all that apply.</p>
                <div className="flex flex-wrap gap-2.5">
                  {CHALLENGES.map((c) => {
                    const active = challenges.includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`audit-challenge-${slug(c)}`}
                        onClick={() => toggleChallenge(c)}
                        aria-pressed={active}
                        className={pillCls(active)}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7">
                <h2 className="font-heading font-bold text-lg text-white">
                  Want a more financially specific audit?
                </h2>
                <p className="mt-1.5 text-sm text-slate-400">
                  These numbers are optional. Rough estimates are fine.
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <label htmlFor="average_customer_value" className={labelCls}>Approximate gross commission income per closed transaction</label>
                    <input id="average_customer_value" data-testid="audit-customer-value" type="number" min="0" inputMode="decimal" value={form.average_customer_value} onChange={set("average_customer_value")} placeholder="e.g. 9000" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="monthly_inquiries" className={labelCls}>Approximate number of new leads per month (as a number)</label>
                    <input id="monthly_inquiries" data-testid="audit-monthly-inquiries" type="number" min="0" inputMode="decimal" value={form.monthly_inquiries} onChange={set("monthly_inquiries")} placeholder="e.g. 30" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="estimated_close_rate" className={labelCls}>Approximate percentage of leads that become clients</label>
                    <input id="estimated_close_rate" data-testid="audit-close-rate" type="number" min="0" max="100" inputMode="decimal" value={form.estimated_close_rate} onChange={set("estimated_close_rate")} placeholder="e.g. 10" className={inputCls} />
                  </div>
                </div>
              </div>

              {error && (
                <p data-testid="audit-form-error" className="text-sm text-red-300">{error}</p>
              )}

              <button
                type="submit"
                data-testid="audit-submit-button"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-indigo-600 hover:bg-indigo-500 font-semibold text-white text-lg transition-[background-color,transform,box-shadow,opacity] duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(99,102,241,0.45)] disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? "Submitting…" : "Request My Free Real Estate Lead Flow Audit"}
              </button>
              <p className="text-xs text-slate-500 text-center">
                No obligation. Your information is used only to prepare and deliver your audit.
              </p>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
